import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Grid,
  FormControl,
  FormLabel,
  Tooltip,
} from '@mui/material';
import { CalendarToday, CopyAll } from '@mui/icons-material';
import {
  getAddress,
  getPubKey,
  getContentHash,
} from 'src/utils/contracts/resolver';
import { getOwner, getResolver } from 'src/utils/contracts/registry';
import { nameExpires } from 'src/utils/contracts/registrar';
import { Chain } from 'src/types/enums';

const Information: React.FC<{
  setRegisterState: (state: RegisterState) => void;
  domainName: string;
}> = ({ setRegisterState, domainName }) => {
  const [owner, setOwner] = useState<string>('');
  const [expirationDate, setExpirationDate] = useState<string>('');

  const [domainInfo, setDomainInfo] = useState<
    { label: string; value: string }[]
  >([]);

  const [tooltipText, setTooltipText] = useState('Click to copy');

  const handleCopyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);
    setTooltipText('Copied!');
    setTimeout(() => setTooltipText('Click to copy'), 1500);
  };

  useEffect(() => {
    const fetchData = async () => {
      const domainArr = domainName.split('.');

      // Domain Settings
      setOwner(await getOwner(domainArr));

      // Domain Expiration
      const formattedDate = await nameExpires(domainArr[domainArr.length - 2]);
      setExpirationDate(formattedDate);

      const address = await getResolver(domainArr);

      if (address.length > 0) {
        const values = [];
        // Get Address
        const resolvedAddress = await getAddress(domainArr, address, Chain.C);
        if (resolvedAddress && resolvedAddress.length > 0) {
          values.push({ label: 'Address', value: resolvedAddress });
        }
        // Get Public Key
        const pubKey = await getPubKey(domainArr, address);
        if (pubKey.length > 0) {
          values.push({ label: 'Public Key', value: pubKey });
        }
        // Get Content Hash
        const contentHash = await getContentHash(domainArr, address);
        if (contentHash.length > 0 && contentHash !== '0x') {
          values.push({ label: 'Content Hash', value: contentHash });
        }

        setDomainInfo(values);
      }
    };
    setOwner('');
    setExpirationDate('');
    setDomainInfo([]);

    fetchData();
  }, [domainName]);

  return (
    <Container
      maxWidth="sm"
      sx={{
        maxHeight: '90%',
        overflow: 'auto',
      }}
    >
      <Box display="flex" justifyContent="space-between">
        <Button
          onClick={() =>
            setRegisterState({
              domainName: '',
              status: 'searching',
            })
          }
          sx={{
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'grey.800',
              borderColor: 'grey.600',
            },
          }}
        >
          <Typography variant="body1">Return</Typography>
        </Button>
        <Typography variant="h5">
          {domainName.split('.').map((part, index, array) => (
            <React.Fragment key={`${part}-${index}`}>
              {index < array.length - 1 ? (
                <span
                  style={{
                    color: '#66C9E8',
                  }}
                >
                  {part}
                </span>
              ) : (
                <span key={`${part}-${index}`}>.{part}</span>
              )}
              {index < array.length - 2 && '.'}
            </React.Fragment>
          ))}
        </Typography>

        <Tooltip title={tooltipText} arrow>
          <Typography
            variant="body1"
            paddingTop={0.8}
            onClick={() => handleCopyToClipboard(owner)}
            style={{ cursor: 'pointer' }}
          >
            {owner.slice(0, 6)} ... {owner.slice(-5)}
          </Typography>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flexDirection: 'row',
          border: '1px solid',
          borderColor: 'grey.600',
          borderRadius: '8px',
          padding: '24px',
          height: '60vh',
          overflow: 'auto',
        }}
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="center"
          direction="row"
          rowSpacing={5}
        >
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Expiration Date"
              variant="outlined"
              value={expirationDate}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '46px',
                  '&:hover fieldset': {
                    borderColor: 'grey.600',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'grey.600',
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarToday sx={{ color: '#9c9c9c' }} />
                  </InputAdornment>
                ),
                sx: { color: '#fff' },
              }}
              InputLabelProps={{
                shrink: true,
              }}
              disabled
            />
          </Grid>
          {domainInfo.map(info => (
            <Grid item xs={12} key={info.label}>
              <FormControl fullWidth>
                <FormLabel
                  sx={{
                    position: 'absolute',
                    transform: 'translateY(-24px)',
                    pointerEvents: 'none',
                  }}
                >
                  {info.label}
                </FormLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={info.value}
                  disabled={true}
                  InputProps={{
                    endAdornment: (
                      <Tooltip title={tooltipText} arrow>
                        <InputAdornment position="end">
                          <CopyAll
                            onClick={() => handleCopyToClipboard(info.value)}
                            style={{ cursor: 'pointer' }}
                            sx={{ color: '#9c9c9c' }}
                          />
                        </InputAdornment>
                      </Tooltip>
                    ),
                    sx: { color: '#fff' },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '48px',
                      '&:hover fieldset': {
                        borderColor: 'grey.600',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'grey.600',
                      },
                    },
                  }}
                />
              </FormControl>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default Information;
